/**
 * Data layer for `agency-dashboard`.
 *
 * Backend (apps/api/src/routes/agency-dashboard.ts) exposes a single report
 * endpoint that rolls every sub-tenant's click/conversion/revenue/payout
 * totals up into one response:
 *
 *   GET /api/v1/agency-dashboard/summary?range=7d|30d|90d
 *        -> { range, clients: ClientRow[], totals: ClientTotals }
 *
 * Raw `fetch()` is confined to this file per the dash contract (D6).
 */

export type RangeKey = "7d" | "30d" | "90d";

export interface ClientRow {
	tenant_id: string;
	name: string;
	clicks: number;
	conversions: number;
	revenue: number;
	payouts: number;
	cr: number;
	epc: number;
}

export interface ClientTotals {
	clients: number;
	clicks: number;
	conversions: number;
	revenue: number;
	payouts: number;
	cr: number;
	epc: number;
}

export interface AgencySummary {
	range: RangeKey;
	clients: ClientRow[];
	totals: ClientTotals;
}

const BASE = "/api/v1/agency-dashboard";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

/** Fetch the agency-wide rollup for a range. */
export async function fetchAgencySummary(
	range: RangeKey,
): Promise<AgencySummary> {
	const res = await fetch(
		`${BASE}/summary?range=${encodeURIComponent(range)}`,
		{
			method: "GET",
			credentials: "include",
			headers: { accept: "application/json" },
		},
	);
	return parseOrThrow<AgencySummary>(res);
}

// ---------------------------------------------------------------------------
// Formatters — co-located so the page and tests import once.
// ---------------------------------------------------------------------------

export function formatInteger(n: number): string {
	if (!Number.isFinite(n)) return "0";
	return Math.round(n).toLocaleString("en-US");
}

export function formatMoney(n: number): string {
	if (!Number.isFinite(n)) return "$0.00";
	return n.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
		maximumFractionDigits: 2,
	});
}

export function formatCrPercent(ratio: number): string {
	if (!Number.isFinite(ratio) || ratio <= 0) return "0.00%";
	return `${(ratio * 100).toFixed(2)}%`;
}

export function formatEpc(n: number): string {
	if (!Number.isFinite(n)) return "$0.00";
	return n.toLocaleString("en-US", {
		style: "currency",
		currency: "USD",
		minimumFractionDigits: 2,
		maximumFractionDigits: 4,
	});
}

// ---------------------------------------------------------------------------
// CSV export — turns the rollup into a downloadable CSV string. Exported for
// tests; the page wires the click handler that calls this + triggers a download.
// ---------------------------------------------------------------------------

export function toCsv(summary: AgencySummary): string {
	const header = [
		"tenant_id",
		"name",
		"clicks",
		"conversions",
		"revenue",
		"payouts",
		"cr",
		"epc",
	].join(",");
	const rows = summary.clients.map((c) =>
		[
			csvField(c.tenant_id),
			csvField(c.name),
			c.clicks,
			c.conversions,
			c.revenue.toFixed(2),
			c.payouts.toFixed(2),
			c.cr.toFixed(6),
			c.epc.toFixed(6),
		].join(","),
	);
	return [header, ...rows].join("\n");
}

function csvField(s: string): string {
	if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
	return s;
}

export const agencyDashboardQueryKeys = {
	all: ["agency-dashboard"] as const,
	summary: (range: RangeKey) =>
		[...agencyDashboardQueryKeys.all, "summary", range] as const,
};
